import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { MessageSquare, Star } from 'lucide-react';
import { useBranding } from '@/shared/contexts/BrandingContext';
import { useAuth } from '@/shared/hooks/useAuth';
import { useToast } from '@/shared/hooks/use-toast';

interface FeedbackFormData {
  feedback_type: 'bug' | 'suggestion' | 'complaint' | 'praise';
  rating: number;
  feedback_text: string;
}

export default function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<FeedbackFormData>({
    feedback_type: 'suggestion',
    rating: 5,
    feedback_text: ''
  });

  const { currentBrand } = useBranding();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get device info for analytics
  const getDeviceInfo = () => {
    return {
      userAgent: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      isMobile: window.innerWidth <= 768,
      timestamp: new Date().toISOString(),
      language: navigator.language,
      platform: navigator.platform
    };
  };

  // Determine user role based on current brand and routes
  const getUserRole = () => {
    if (currentBrand.type === 'greenshield') {
      return 'technician';
    } else if (currentBrand.type === 'aegis360') {
      return 'manager';
    }
    return 'user';
  };

  const submitFeedbackMutation = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      const response = await fetch('/api/v1/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: (user as any)?.id || null,
          user_role: getUserRole(),
          brand: currentBrand.type,
          page_url: window.location.href,
          feedback_type: data.feedback_type,
          rating: data.rating,
          feedback_text: data.feedback_text,
          device_info: getDeviceInfo()
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Thank you for your feedback!",
        description: "Your feedback helps us improve the platform.",
      });
      setIsOpen(false);
      setFormData({
        feedback_type: 'suggestion',
        rating: 5,
        feedback_text: ''
      });
    },
    onError: (error) => {
      toast({
        title: "Error submitting feedback",
        description: "Please try again later.",
        variant: "destructive",
      });
      console.error('Feedback submission error:', error);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.feedback_text.trim()) {
      toast({
        title: "Please provide feedback text",
        description: "We need your feedback to help improve the platform.",
        variant: "destructive",
      });
      return;
    }
    submitFeedbackMutation.mutate(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`fixed bottom-6 left-6 z-40 shadow-lg ${
            currentBrand.type === 'aegis360' 
              ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' 
              : 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600'
          }`}
          data-testid="button-feedback-widget"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Send Feedback
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <MessageSquare className={`h-5 w-5 ${
              currentBrand.type === 'aegis360' ? 'text-blue-600' : 'text-emerald-600'
            }`} />
            <span>Share Your Feedback</span>
          </DialogTitle>
          <DialogDescription>
            Help us improve {currentBrand.name} by sharing your experience and suggestions.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="form-feedback">
          {/* Feedback Type */}
          <div className="space-y-2">
            <Label htmlFor="feedback-type">What type of feedback is this?</Label>
            <Select 
              value={formData.feedback_type} 
              onValueChange={(value: any) => setFormData(prev => ({...prev, feedback_type: value}))}
            >
              <SelectTrigger data-testid="select-feedback-type">
                <SelectValue placeholder="Select feedback type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="suggestion">💡 Suggestion / Improvement</SelectItem>
                <SelectItem value="bug">🐛 Bug / Technical Issue</SelectItem>
                <SelectItem value="complaint">😞 Problem / Complaint</SelectItem>
                <SelectItem value="praise">👏 Praise / What's Working</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Rating */}
          <div className="space-y-3">
            <Label>How would you rate your overall experience?</Label>
            <RadioGroup
              value={formData.rating.toString()}
              onValueChange={(value) => setFormData(prev => ({...prev, rating: parseInt(value)}))}
              className="flex space-x-4"
            >
              {[1, 2, 3, 4, 5].map((num) => (
                <div key={num} className="flex items-center space-x-1">
                  <RadioGroupItem value={num.toString()} id={`rating-${num}`} data-testid={`radio-rating-${num}`} />
                  <Label htmlFor={`rating-${num}`} className="cursor-pointer flex items-center">
                    <Star className={`h-4 w-4 ${num <= formData.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                    <span className="ml-1 text-sm">{num}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Feedback Text */}
          <div className="space-y-2">
            <Label htmlFor="feedback-text">
              {formData.feedback_type === 'bug' ? 'Describe the issue and steps to reproduce:' :
               formData.feedback_type === 'suggestion' ? 'What would you like to see improved?' :
               formData.feedback_type === 'complaint' ? 'What went wrong? How can we fix it?' :
               'What did you like? Tell us more!'}
            </Label>
            <Textarea
              id="feedback-text"
              value={formData.feedback_text}
              onChange={(e) => setFormData(prev => ({...prev, feedback_text: e.target.value}))}
              placeholder={
                formData.feedback_type === 'bug' 
                  ? "I was trying to create a quote when I clicked 'Save' and got an error..."
                  : formData.feedback_type === 'suggestion'
                  ? "It would be great if the system could automatically..."
                  : "Please share your feedback here..."
              }
              rows={4}
              required
              data-testid="textarea-feedback"
            />
          </div>

          {/* Context Information */}
          <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
            <strong>Context:</strong> {currentBrand.name} • {getUserRole()} • {window.location.pathname}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitFeedbackMutation.isPending}
              className={currentBrand.type === 'aegis360' 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-emerald-600 hover:bg-emerald-700'
              }
              data-testid="button-submit-feedback"
            >
              {submitFeedbackMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}